/**
 * Normalize various date formats to YYYY-MM-DD
 * Handles: MM/DD/YY, M/D/YY, YYYY年MM月DD日, YYYY-MM-DD, etc.
 */
export function normalizeDateToYYYYMMDD(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') {
    return new Date().toISOString().split('T')[0];
  }

  const trimmed = dateStr.trim();

  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Chinese format: 2025年07月22日 or 2025年7月22日
  const chineseMatch = trimmed.match(/(\d{4})[年\-\/](\d{1,2})[月\-\/](\d{1,2})/);
  if (chineseMatch) {
    const year = chineseMatch[1];
    const month = chineseMatch[2].padStart(2, '0');
    const day = chineseMatch[3].padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // American format: MM/DD/YY or M/D/YY
  const americanMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (americanMatch) {
    let month = americanMatch[1].padStart(2, '0');
    let day = americanMatch[2].padStart(2, '0');
    let year = americanMatch[3];

    // Handle 2-digit year (assume 20xx for years 00-99)
    if (year.length === 2) {
      const yearNum = parseInt(year);
      // If year is 00-30, assume 2000-2030, else 1900-1999
      year = yearNum <= 30 ? `20${year}` : `19${year}`;
    }

    return `${year}-${month}-${day}`;
  }

  // ISO format with time: 2025-10-14T03:20:47.000Z
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  // YYYYMMDD format
  const compactMatch = trimmed.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compactMatch) {
    return `${compactMatch[1]}-${compactMatch[2]}-${compactMatch[3]}`;
  }

  // YYYY/MM/DD format
  const slashMatch = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (slashMatch) {
    const year = slashMatch[1];
    const month = slashMatch[2].padStart(2, '0');
    const day = slashMatch[3].padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Try parsing as Date object as last resort
  try {
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
  } catch (e) {
    console.warn('Failed to parse date:', trimmed);
  }

  // If all else fails, return today's date
  console.warn(`Could not parse date: "${dateStr}", using today's date`);
  return new Date().toISOString().split('T')[0];
}
